import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Notify, Confirm, Loading } from 'notiflix';
import moment from 'moment';
import {
  Box,
  Button,
  Card,
  Chip,
  IconButton,
  Tooltip,
  Typography,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  AddCircle,
  FilterList,
  Refresh
} from '@mui/icons-material';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import DataTable from 'examples/Tables/DataTable';
import { subscriptionApi } from 'api/subscriptionApi';
import { SubscriptionTypes, SubscriptionTypeLabels } from 'constants/subscriptionTypes';
import usePermission from 'hooks/usePermission';
import NoPermissionNotice from 'components/NoPermissionNotice';

const SubscriptionTypeList = () => {
  const navigate = useNavigate();
  const canCreate = usePermission('shop-management', 'create');
  const canRead = usePermission('shop-management', 'read');
  const canUpdate = usePermission('shop-management', 'update');
  const canDelete = usePermission('shop-management', 'delete');

  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState(null);
  const [countries, setCountries] = useState([]);
  const [durations, setDurations] = useState([]);
  
  // Filter states
  const [filters, setFilters] = useState({
    subscriptionType: '',
    moduleType: '',
    country: '',
    duration: '',
    page: 1,
    limit: 10,
    sortBy: 'createdAt',
    sortOrder: 'DESC'
  });
  
  const [totalPages, setTotalPages] = useState(1);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    oneTime: 0,
    subscription: 0
  });

  const formatDuration = (duration) => {
    if (!duration) return 'N/A';
    
    if (typeof duration === 'object') {
      return duration.name || `${duration.value || ''} ${duration.unit || ''}`.trim() || 'N/A';
    }
    
    return duration;
  };

  const formatCountry = (country) => {
    if (!country) return 'All';
    
    if (typeof country === 'object') {
      return country.name || country.countryCode || 'All';
    }
    
    return country;
  };

  const formatSubscriptionType = (type) => {
    if (!type) return 'N/A';
    
    const typeLower = type.toLowerCase();
    const label = SubscriptionTypeLabels[type] || SubscriptionTypeLabels[typeLower];
    
    if (label) {
      return {
        displayName: label,
        originalName: type
      };
    }
    
    const formattedName = type
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    
    return {
      displayName: formattedName,
      originalName: type
    };
  };

  const getModuleTypeLabel = (moduleType) => {
    if (!moduleType) return 'N/A';
    
    const moduleTypes = {
      valuepack: 'Value Pack',
      product: 'Product',
      service: 'Service',
      saas: 'SaaS'
    };
    
    return moduleTypes[moduleType.toLowerCase()] || 
      moduleType.split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
  };

  const getStatusInfo = (subscription) => {
    const isActive = subscription.isActive !== false;
    
    return {
      label: isActive ? 'Active' : 'Inactive',
      color: isActive ? 'success' : 'error',
      variant: isActive ? 'filled' : 'outlined'
    };
  };

  const getPricingStrategyChip = (strategy) => {
    if (!strategy) return null;
    
    const strategyColors = {
      global: { color: 'primary', bg: '#e3f2fd' },
      custom: { color: 'secondary', bg: '#f3e5f5' }
    };
    
    const colorInfo = strategyColors[strategy] || { color: 'default', bg: '#f5f5f5' };
    
    return (
      <Chip 
        label={strategy.charAt(0).toUpperCase() + strategy.slice(1)}
        size="small"
        sx={{ 
          backgroundColor: colorInfo.bg,
          color: colorInfo.color === 'primary' ? '#1976d2' : 
                 colorInfo.color === 'secondary' ? '#9c27b0' : '#616161',
          fontWeight: 500,
          fontSize: '0.7rem'
        }}
      />
    );
  };

  const getConfigTypeChip = (configType, targetEntityType) => {
    if (!configType) return null;
    
    const configTypeColors = {
      global: { color: 'primary', bg: '#e3f2fd' },
      entity_specific: { color: 'secondary', bg: '#f3e5f5' }
    };
    
    const colorInfo = configTypeColors[configType] || { color: 'default', bg: '#f5f5f5' };
    const label = configType === 'entity_specific' 
      ? `Entity (${targetEntityType || 'N/A'})` 
      : 'Global';
    
    return (
      <Chip 
        label={label}
        size="small"
        sx={{ 
          backgroundColor: colorInfo.bg,
          color: colorInfo.color === 'primary' ? '#1976d2' : 
                 colorInfo.color === 'secondary' ? '#9c27b0' : '#616161',
          fontWeight: 500,
          fontSize: '0.65rem',
          height: '20px'
        }}
      />
    );
  };

  const fetchSubscriptions = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await subscriptionApi.getAllSubscriptions({
        ...filters,
        page: filters.page,
        limit: filters.limit
      });
      
      const subscriptionsData = response.configs || [];
      const totalCount = response.pagination?.totalItems || subscriptionsData.length;
      setTotalPages(response.pagination?.totalPages || 1);
      
      setSubscriptions(Array.isArray(subscriptionsData) ? subscriptionsData : []);
      
      // Calculate stats
      if (Array.isArray(subscriptionsData)) {
        const activeCount = subscriptionsData.filter(sub => sub.isActive !== false).length;
        const oneTimeCount = subscriptionsData.filter(sub => {
          const type = sub.subscriptionType?.toLowerCase();
          return type === 'one_time' || type === SubscriptionTypes.ONE_TIME;
        }).length;
        const subscriptionCount = subscriptionsData.filter(sub => {
          const type = sub.subscriptionType?.toLowerCase();
          return type !== 'one_time' && type !== SubscriptionTypes.ONE_TIME;
        }).length;
        
        setStats({
          total: totalCount,
          active: activeCount,
          oneTime: oneTimeCount,
          subscription: subscriptionCount
        });
      }
      
    } catch (err) {
      console.error('Error fetching subscriptions:', err);
      setError(err.message || 'Failed to load subscriptions');
      Notify.failure(err.message || 'Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!canRead) {
      setLoading(false);
      return;
    }
    fetchSubscriptions();
  }, [filters.page, filters.subscriptionType, filters.moduleType, filters.country, filters.duration, canRead]);

  useEffect(() => {
    if (subscriptions.length > 0) {
      const uniqueCountries = [];
      const uniqueDurations = [];
      const countryMap = new Map();
      const durationMap = new Map();
      
      subscriptions.forEach(sub => {
        // Extract countries
        if (sub.country && typeof sub.country === 'object') {
          if (!countryMap.has(sub.country.id)) {
            countryMap.set(sub.country.id, sub.country);
            uniqueCountries.push(sub.country);
          }
        }
        
        // Extract durations
        if (sub.duration && typeof sub.duration === 'object') {
          if (!durationMap.has(sub.duration.id)) {
            durationMap.set(sub.duration.id, sub.duration);
            uniqueDurations.push(sub.duration);
          }
        }
      });
      
      setCountries(uniqueCountries);
      setDurations(uniqueDurations);
    }
  }, [subscriptions]);

  const handleDelete = async (id) => {
    Confirm.show(
      'Delete Subscription Configuration',
      'Are you sure you want to delete this subscription configuration? This action cannot be undone.',
      'Delete',
      'Cancel',
      async () => {
        setDeletingId(id);
        Loading.circle('Deleting subscription configuration...');
        
        try {
          await subscriptionApi.deleteSubscription(id);
          Notify.success('Subscription configuration deleted successfully');
          fetchSubscriptions();
        } catch (error) {
          Notify.failure(error.message || 'Failed to delete subscription configuration');
        } finally {
          Loading.remove();
          setDeletingId(null);
        }
      },
      {
        okButtonBackground: '#ff6b6b',
        titleColor: '#ff6b6b',
      }
    );
  };

  const handleView = (id) => {
    navigate(`/admin/subscription-type/${id}`);
  };

  const handleEdit = (id) => {
    navigate(`/admin/subscription-type/edit/${id}`);
  };

  const columns = [
    { Header: 'Subscription Type', accessor: 'type', width: '18%', align: 'left' },
    { Header: 'Module', accessor: 'module', width: '12%', align: 'left' },
    { Header: 'Duration', accessor: 'duration', width: '10%', align: 'center' },
    { Header: 'Country', accessor: 'country', width: '10%', align: 'center' },
    { Header: 'Config Type', accessor: 'configType', width: '10%', align: 'center' },
    { Header: 'Pricing', accessor: 'pricing', width: '12%', align: 'center' },
    { Header: 'Status', accessor: 'status', width: '8%', align: 'center' },
    { Header: 'Created', accessor: 'created', width: '10%', align: 'center' },
    { Header: 'Actions', accessor: 'actions', width: '10%', align: 'center' },
  ];

  const getRows = () => {
    if (!Array.isArray(subscriptions)) return [];
    
    return subscriptions.map((subscription) => {
      const isDeleting = deletingId === subscription.id;
      const statusInfo = getStatusInfo(subscription);
      const typeInfo = formatSubscriptionType(subscription.subscriptionType);
      
      return {
        type: (
          <Box>
            <MDTypography variant="caption" fontWeight="medium" color="text">
              {typeInfo.displayName}
            </MDTypography>
            {/* <Typography variant="caption" color="textSecondary" display="block">
              <code style={{ fontSize: '0.7rem', color: '#666' }}>
                {typeInfo.originalName}
              </code>
            </Typography> 
            {subscription.framework && (
              <Typography variant="caption" color="textSecondary" display="block">
                Framework: {subscription.framework.name}
              </Typography>
            )} */}
          </Box>
        ),
        module: (
          <MDTypography variant="caption" color="text">
            {getModuleTypeLabel(subscription.moduleType)}
          </MDTypography>
        ),
        duration: (
          <Box textAlign="center">
            <MDTypography variant="caption" color="text">
              {formatDuration(subscription.duration)}
            </MDTypography>
            {/* {subscription.duration?.value && subscription.duration?.unit && (
              <Typography variant="caption" color="textSecondary" display="block">
                {subscription.duration.value} {subscription.duration.unit}
              </Typography>
            )} */}
          </Box>
        ),
        country: (
          <Box textAlign="center">
            <MDTypography variant="caption" color="text">
              {formatCountry(subscription.country)}
            </MDTypography>
            {subscription.country?.currencySymbol && (
              <Typography variant="caption" color="textSecondary" display="block">
                {subscription.country.currencySymbol}
              </Typography>
            )}
          </Box>
        ),
        configType: (
          <Box display="flex" justifyContent="center">
            {getConfigTypeChip(subscription.configType, subscription.targetEntityType)}
          </Box>
        ),
        pricing: (
          <Box display="flex" flexDirection="column" alignItems="center" gap={0.5}>
            <Box display="flex" alignItems="center">
              {getPricingStrategyChip(subscription.pricing?.strategy)}
              {/* {subscription.pricing?.strategy === 'custom' && subscription.pricing?.discountRules?.length > 0 && (
                <Tooltip title={`${subscription.pricing.discountRules.length} custom pricing rules`}>
                  <Chip 
                    label={`${subscription.pricing.discountRules.length}`}
                    size="small"
                    sx={{ 
                      ml: 0.5,
                      backgroundColor: '#f3e5f5',
                      color: '#9c27b0',
                      fontSize: '0.6rem',
                      height: '18px'
                    }}
                  />
                </Tooltip>
              )} */}
            </Box>
            {subscription.pricing?.globalDiscount && parseFloat(subscription.pricing.globalDiscount) > 0 && (
              <Typography variant="caption" color="success.main">
                {parseFloat(subscription.pricing.globalDiscount)}% off
              </Typography>
            )}
          </Box>
        ),
        status: (
          <Chip 
            label={statusInfo.label}
            size="small"
            color={statusInfo.color}
            variant={statusInfo.variant}
          />
        ),
        created: (
          <Box textAlign="center">
            <MDTypography variant="caption" color="text">
              {subscription.createdAt ? moment(subscription.createdAt).format('MMM D') : 'N/A'}
            </MDTypography>
            <Typography variant="caption" color="textSecondary" display="block">
              {subscription.createdAt ? moment(subscription.createdAt).format('YYYY') : ''}
            </Typography>
          </Box>
        ),
        actions: (
          <Box display="flex" gap={0.5} justifyContent="center">
            {canRead && (
              <Tooltip title="View Details">
                <IconButton
                  size="small"
                  onClick={() => handleView(subscription.id)}
                  disabled={isDeleting}
                >
                  <VisibilityIcon color="info" fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {canUpdate && (
              <Tooltip title="Edit">
                <IconButton
                  size="small"
                  onClick={() => handleEdit(subscription.id)}
                  disabled={isDeleting}
                >
                  <EditIcon color="primary" fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {canDelete && (
              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  onClick={() => handleDelete(subscription.id)}
                  disabled={isDeleting}
                >
                  {isDeleting ? (
                    <CircularProgress size={16} />
                  ) : (
                    <DeleteIcon color="error" fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ),
      };
    });
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value,
      page: 1
    }));
  };

  // Page changes must NOT go through handleFilterChange (it resets page to 1).
  const handlePageChange = (page) => {
    setFilters(prev => ({ ...prev, page }));
  };

  // Page numbers with ellipsis — same logic as the Pages list.
  const getPageNumbers = () => {
    const currentPage = filters.page;
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const pageList = [1];
    if (currentPage > 3) pageList.push('...');
    for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
      pageList.push(i);
    }
    if (currentPage < totalPages - 2) pageList.push('...');
    pageList.push(totalPages);
    return pageList;
  };

  const handleRefresh = () => {
    fetchSubscriptions();
    Notify.info('Refreshing subscription list...');
  };

  const getSubscriptionTypeOptions = () => {
    const options = [
      { value: '', label: 'All Types' }
    ];
    
    Object.entries(SubscriptionTypes).forEach(([key, value]) => {
      const label = SubscriptionTypeLabels[value] || key;
      options.push({ 
        value, 
        label: `${label} (${value})`
      });
    });
    
    return options;
  };

  if (!canRead) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <NoPermissionNotice message="Your role doesn't include read access to shop management. Ask an administrator to update your permissions." />
      </DashboardLayout>
    );
  }

  if (loading && subscriptions.length === 0) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <Box pt={6} pb={3} display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <Box textAlign="center">
            <CircularProgress />
            <Typography variant="h6" mt={2}>Loading subscription configurations...</Typography>
          </Box>
        </Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Card>
          <MDBox
            mx={2}
            mt={-3}
            py={3}
            px={2}
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="info"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <MDTypography variant="h6" color="white">
              Subscription Configurations ({stats.total})
            </MDTypography>

            <Box mb={2} display="flex" justifyContent="flex-end" gap={2}>
              <Button
                variant="contained"
                onClick={handleRefresh}
                startIcon={<Refresh />}
                sx={{
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  color: '#fff',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.3)',
                  },
                }}
              >
                Refresh
              </Button>
              
              {canCreate && (
                <Button
                  variant="contained"
                  onClick={() => navigate('/admin/subscription-type/create')}
                  startIcon={<AddCircle />}
                  sx={{
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    color: '#fff',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.3)',
                    },
                  }}
                >
                  Create New
                </Button>
              )}
            </Box>
          </MDBox>

          {/* Stats Cards */}
          <MDBox p={2}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ p: 2, bgcolor: '#e3f2fd' }}>
                  <Typography variant="h6" color="primary" gutterBottom>
                    Total
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {stats.total}
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ p: 2, bgcolor: '#e8f5e9' }}>
                  <Typography variant="h6" color="success" gutterBottom>
                    Active
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="success">
                    {stats.active}
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ p: 2, bgcolor: '#fff3e0' }}>
                  <Typography variant="h6" color="warning" gutterBottom>
                    One-Time
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="warning">
                    {stats.oneTime}
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ p: 2, bgcolor: '#f3e5f5' }}>
                  <Typography variant="h6" color="secondary" gutterBottom>
                    Subscriptions
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="secondary">
                    {stats.subscription}
                  </Typography>
                </Card>
              </Grid>
            </Grid>
          </MDBox>

          {/* Filters */}
          <MDBox p={2}>
            <Card sx={{ p: 2, mb: 2 }}>
              <Box display="flex" alignItems="center" mb={2}>
                <FilterList sx={{ mr: 1, color: '#64748b' }} />
                <Typography variant="subtitle1" fontWeight="medium">
                  Filters
                </Typography>
              </Box>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Subscription Type</InputLabel>
                    <Select
                      value={filters.subscriptionType}
                      onChange={(e) => handleFilterChange('subscriptionType', e.target.value)}
                      label="Subscription Type"
                    >
                      {getSubscriptionTypeOptions().map(option => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Module Type</InputLabel>
                    <Select
                      value={filters.moduleType}
                      onChange={(e) => handleFilterChange('moduleType', e.target.value)}
                      label="Module Type"
                    >
                      <MenuItem value="">All Modules</MenuItem>
                      <MenuItem value="valuepack">Value Pack</MenuItem>
                      <MenuItem value="product">Product</MenuItem>
                      <MenuItem value="service">Service</MenuItem>
                      <MenuItem value="saas">SaaS</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Country</InputLabel>
                    <Select
                      value={filters.country}
                      onChange={(e) => handleFilterChange('country', e.target.value)}
                      label="Country"
                    >
                      <MenuItem value="">All Countries</MenuItem>
                      {countries.map(country => (
                        <MenuItem key={country.id} value={country.id}>
                          {country.name} ({country.countryCode})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Duration</InputLabel>
                    <Select
                      value={filters.duration}
                      onChange={(e) => handleFilterChange('duration', e.target.value)}
                      label="Duration"
                    >
                      <MenuItem value="">All Durations</MenuItem>
                      {durations.map(duration => (
                        <MenuItem key={duration.id} value={duration.id}>
                          {formatDuration(duration)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Card>
          </MDBox>

          {/* Error Alert */}
          {error && (
            <MDBox mx={2} mb={2}>
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            </MDBox>
          )}

          {/* Data Table */}
          <MDBox pt={3}>
            {loading ? (
              <Box 
                display="flex" 
                justifyContent="center" 
                alignItems="center" 
                py={6}
                flexDirection="column"
              >
                <CircularProgress />
                <Typography variant="h6" mt={2}>
                  Loading subscriptions...
                </Typography>
              </Box>
            ) : !Array.isArray(subscriptions) || subscriptions.length === 0 ? (
              <Box 
                display="flex" 
                justifyContent="center" 
                alignItems="center" 
                py={6}
                flexDirection="column"
              >
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  No subscription configurations found
                </Typography>
                <Typography variant="body2" color="textSecondary" mb={2}>
                  {filters.subscriptionType || filters.moduleType || filters.country || filters.duration 
                    ? 'Try changing your filters' 
                    : 'Create your first subscription configuration to get started'}
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => navigate('/admin/subscription-type/create')}
                  startIcon={<AddCircle />}
                  sx={{ color: "#fff" }}
                >
                  Create Subscription Configuration
                </Button>
              </Box>
            ) : (
              <DataTable
                table={{ columns, rows: getRows() }}
                isSorted={false}
                entriesPerPage={false}
                showTotalEntries={false}
                noEndBorder
              />
            )}
            
            {/* Server-side pagination */}
            {stats.total > 0 && (
              <MDBox
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                px={3}
                py={2}
                sx={{ borderTop: '1px solid #e0e0e0' }}>
                <MDTypography variant="caption" color="text" sx={{ mb: { xs: 1, md: 0 } }}>
                  Showing{' '}
                  {Math.min((filters.page - 1) * filters.limit + 1, stats.total)}
                  –{Math.min(filters.page * filters.limit, stats.total)} of {stats.total} configurations
                </MDTypography>

                <MDBox display="flex" alignItems="center" gap={1}>
                  <MDButton
                    variant="outlined"
                    color="info"
                    size="small"
                    disabled={filters.page <= 1}
                    onClick={() => handlePageChange(filters.page - 1)}>
                    Previous
                  </MDButton>

                  {getPageNumbers().map((item, idx) =>
                    item === '...' ? (
                      <MDTypography
                        key={`dots-${idx}`}
                        variant="caption"
                        sx={{ px: 0.5, userSelect: 'none' }}>
                        …
                      </MDTypography>
                    ) : (
                      <MDButton
                        key={item}
                        variant={item === filters.page ? 'gradient' : 'outlined'}
                        color="info"
                        size="small"
                        sx={{ minWidth: 36 }}
                        onClick={() => handlePageChange(item)}>
                        {item}
                      </MDButton>
                    ),
                  )}

                  <MDButton
                    variant="outlined"
                    color="info"
                    size="small"
                    disabled={filters.page >= totalPages}
                    onClick={() => handlePageChange(filters.page + 1)}>
                    Next
                  </MDButton>
                </MDBox>
              </MDBox>
            )}
          </MDBox>
        </Card>
      </MDBox>
    </DashboardLayout>
  );
};

export default SubscriptionTypeList;
